import { useState } from 'react';
import Button from '../base/Button';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showContactInfo, setShowContactInfo] = useState(false);
  const [activeService, setActiveService] = useState<string | null>(null);

  const navItems = [
    { 
      name: 'Flights', 
      id: 'flights',
      icon: 'ri-flight-takeoff-line',
      description: 'Domestic and international flight bookings',
      features: ['Domestic Flights', 'International Flights', 'Best Price Guarantee', 'Instant Booking']
    },
    { 
      name: 'Hotels', 
      id: 'hotels',
      icon: 'ri-hotel-bed-line',
      description: 'Luxury accommodations across India',
      features: ['5-Star Hotels', 'Boutique Properties', 'Resort Stays', 'Heritage Hotels']
    },
    { 
      name: 'Cars', 
      id: 'cars',
      icon: 'ri-car-line',
      description: 'Premium transportation services',
      features: ['Luxury Sedans', 'SUVs & Tempo Travelers', 'Chauffeur Services', 'Airport Transfers']
    },
    { 
      name: 'Visas', 
      id: 'visas',
      icon: 'ri-passport-line',
      description: 'Complete visa assistance',
      features: ['Tourist Visas', 'Business Visas', 'Documentation Help', 'Fast Track Processing']
    },
    { 
      name: 'Cruises', 
      id: 'cruises',
      icon: 'ri-ship-line',
      description: 'Memorable cruise experiences',
      features: ['River Cruises', 'Coastal Cruises', 'Luxury Liners', 'Dinner Cruises']
    },
    { 
      name: 'Trains', 
      id: 'trains',
      icon: 'ri-train-line',
      description: 'Railway booking services',
      features: ['Premium Trains', 'Sleeper Classes', 'AC Coaches', 'Luxury Train Tours']
    },
    { 
      name: 'Tours', 
      id: 'tours',
      icon: 'ri-map-pin-line',
      description: 'Curated tour packages',
      features: ['Cultural Tours', 'Adventure Tours', 'Spiritual Journeys', 'Custom Itineraries']
    }
  ];

  const handleContactClick = () => {
    setShowContactInfo(true);
  };

  const handleServiceClick = (serviceId: string) => {
    setActiveService(serviceId);
  };

  return (
    <header className="bg-white shadow-lg sticky top-0 z-50">
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between h-24">
          <div className="flex items-center">
            <img 
              src="https://static.readdy.ai/image/11d8e4935baf92f9506f92a91c4432fc/decf9e9f5597a086516483b31d863714.jfif" 
              alt="MaxJourneys Logo" 
              className="h-24 w-auto"
            />
          </div>

          <nav className="hidden md:flex items-center space-x-12">
            {navItems.map((item) => (
              <button
                key={item.name}
                onClick={() => handleServiceClick(item.id)}
                className="text-gray-700 hover:text-blue-600 font-semibold text-xl transition-colors cursor-pointer"
              >
                {item.name}
              </button>
            ))}
          </nav>

          <div className="hidden md:flex items-center">
            <Button size="lg" className="px-8 py-4 text-lg" onClick={handleContactClick}>
              <i className="ri-phone-line mr-3 text-lg"></i>
              Contact Us
            </Button>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-3xl`}></i>
          </button>
        </div>

        {isMenuOpen && (
          <div className="md:hidden bg-white border-t">
            <div className="px-4 pt-4 pb-6 space-y-2">
              {navItems.map((item) => (
                <button
                  key={item.name}
                  onClick={() => handleServiceClick(item.id)}
                  className="block w-full text-left px-4 py-3 text-gray-700 hover:text-blue-600 font-medium text-lg cursor-pointer"
                >
                  {item.name}
                </button>
              ))}
              <div className="px-4 pt-6">
                <Button size="sm" className="w-full py-3 text-lg" onClick={handleContactClick}>
                  <i className="ri-phone-line mr-3"></i>
                  Contact Us
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Service Details Popup */}
      {activeService && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto shadow-2xl">
            {(() => {
              const service = navItems.find(item => item.id === activeService);
              if (!service) return null;
              
              return (
                <>
                  <div className="sticky top-0 bg-white border-b px-8 py-6 flex justify-between items-center">
                    <div className="flex items-center">
                      <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-full mr-4">
                        <i className={`${service.icon} text-2xl text-blue-600`}></i>
                      </div>
                      <div>
                        <h2 className="text-3xl font-bold text-gray-900">{service.name}</h2>
                        <p className="text-gray-600">{service.description}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setActiveService(null)}
                      className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-full hover:bg-gray-100 transition-colors"
                    >
                      <i className="ri-close-line text-2xl"></i>
                    </button>
                  </div>
                  
                  <div className="p-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                      <div>
                        <img 
                          src={`https://readdy.ai/api/search-image?query=professional%20$%7Bservice.name.toLowerCase%28%29%7D%20service%20in%20India%2C%20$%7Bservice.description%7D%2C%20premium%20travel%20services%2C%20modern%20and%20elegant%2C%20high%20quality%20$%7Bservice.name.toLowerCase%28%29%7D%20experience%2C%20Indian%20travel%20industry%2C%20luxury%20and%20comfort&width=600&height=400&seq=${service.id}-service&orientation=landscape`}
                          alt={`${service.name} Service`}
                          className="w-full h-64 object-cover rounded-lg object-top"
                        />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold mb-4 text-blue-600">Our {service.name} Services</h3>
                        <p className="text-gray-700 mb-6 leading-relaxed">
                          Experience premium {service.name.toLowerCase()} services designed to make your journey comfortable and memorable. We offer comprehensive solutions tailored to your specific needs and preferences.
                        </p>
                        <div className="space-y-3">
                          {service.features.map((feature, index) => (
                            <div key={index} className="flex items-center">
                              <i className="ri-check-line text-green-600 mr-3"></i>
                              <span className="text-gray-700 font-medium">{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg mb-8">
                      <h3 className="text-xl font-bold mb-4 text-center">Why Choose Our {service.name} Services?</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                        <div>
                          <i className="ri-star-line text-2xl text-yellow-600 mb-2"></i>
                          <p className="font-semibold">Premium Quality</p>
                          <p className="text-sm text-gray-600">Best-in-class services</p>
                        </div>
                        <div>
                          <i className="ri-price-tag-3-line text-2xl text-green-600 mb-2"></i>
                          <p className="font-semibold">Best Prices</p>
                          <p className="text-sm text-gray-600">Competitive rates guaranteed</p>
                        </div>
                        <div>
                          <i className="ri-customer-service-2-line text-2xl text-blue-600 mb-2"></i>
                          <p className="font-semibold">24/7 Support</p>
                          <p className="text-sm text-gray-600">Round-the-clock assistance</p>
                        </div>
                      </div>
                    </div>

                    <div className="text-center">
                      <Button 
                        size="lg"
                        onClick={handleContactClick}
                        className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 px-8 py-4 text-lg"
                      >
                        <i className="ri-phone-line mr-3"></i>
                        Get Quote for {service.name}
                      </Button>
                      <p className="text-sm text-gray-600 mt-3">
                        Call us for personalized assistance and best deals
                      </p>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Contact Info Popup */}
      {showContactInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Contact Us</h3>
              <button
                onClick={() => setShowContactInfo(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 flex items-center justify-center bg-blue-100 rounded-full">
                  <i className="ri-phone-line text-blue-600"></i>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Phone</p>
                  <a href="tel:+919700644857" className="text-lg font-semibold text-gray-900 hover:text-blue-600 cursor-pointer">
                    +91 9700644857
                  </a>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 flex items-center justify-center bg-blue-100 rounded-full">
                  <i className="ri-mail-line text-blue-600"></i>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <a href="mailto:info@maxjourneys.in" className="text-lg font-semibold text-gray-900 hover:text-blue-600 cursor-pointer">
                    info@maxjourneys.in
                  </a>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 flex items-center justify-center bg-blue-100 rounded-full">
                  <i className="ri-time-line text-blue-600"></i>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Business Hours</p>
                  <p className="text-lg font-semibold text-gray-900">24*7 Available</p>
                </div>
              </div>
            </div>
            
            <div className="mt-6 pt-6 border-t">
              <Button 
                className="w-full" 
                onClick={() => setShowContactInfo(false)}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
